import { hasEffect, hasTurnEffect, vocationOf } from '../selectors';
import type { CombatModifier } from './pipeline';

/** Rotas de barco limitam os dados de ataque. */
export const seaLimit: CombatModifier = {
  id: 'seaLimit',
  dice(ctx) {
    if (!ctx.sea) return;
    const limit = ctx.state.info.ruleset.seaMaxDice;
    if (ctx.attackDice > limit) {
      ctx.attackDice = limit;
      ctx.notes.push('seaLimit');
    }
  },
};

/** Berserk: +N no maior dado de ataque em todos os combates do turno. */
export const strengthRune: CombatModifier = {
  id: 'strengthRune',
  rolls(ctx) {
    if (!hasTurnEffect(ctx.state, ctx.attacker, 'strength') || ctx.attackRolls.length === 0) return;
    ctx.attackRolls[0] += ctx.state.info.ruleset.strengthBonus;
    if (!ctx.notes.includes('strength')) ctx.notes.push('strength');
  },
};

/** Coordenação (regra de era): alvo já atacado neste turno a partir de outro território dá +N no maior dado. */
export const coordination: CombatModifier = {
  id: 'coordination',
  rolls(ctx) {
    const bonus = ctx.state.info.ruleset.coordinationBonus;
    if (!bonus || ctx.kind !== 'territory' || !ctx.to || ctx.attackRolls.length === 0) return;
    const origins = ctx.state.turn.attackedFrom[ctx.to] ?? [];
    if (!origins.some((f) => f !== ctx.from)) return;
    ctx.attackRolls[0] += bonus;
    if (!ctx.notes.includes('coordination')) ctx.notes.push('coordination');
  },
};

/** Rajada de harmonia (vocação de harmonia): +1 no maior dado de ataque até o fim do turno. */
export const flow: CombatModifier = {
  id: 'flow',
  rolls(ctx) {
    if (!hasTurnEffect(ctx.state, ctx.attacker, 'flow') || ctx.attackRolls.length === 0) return;
    ctx.attackRolls[0] += 1;
    if (!ctx.notes.includes('flow')) ctx.notes.push('flow');
  },
  outcome(ctx, out) {
    if (out.attackerLoss > 1 && hasTurnEffect(ctx.state, ctx.attacker, 'flowGuard')) {
      out.attackerLoss = 1;
      if (!ctx.notes.includes('flowGuard')) ctx.notes.push('flowGuard');
    }
  },
};

/** Bullseye: em todos os combates do turno, os empates ficam com o atacante. */
export const precision: CombatModifier = {
  id: 'precision',
  rules(ctx) {
    if (!hasTurnEffect(ctx.state, ctx.attacker, 'precision')) return;
    ctx.tieGoesTo = 'attacker';
    ctx.notes.push('precision');
  },
};

/**
 * Vocação de defesa: no primeiro combate em que defende durante o turno de um
 * adversário, o dado de defesa mais baixo que entra na comparação é rerrolado se
 * fosse perder.
 */
export const defenseVocation: CombatModifier = {
  id: 'defenseVocation',
  rolls(ctx, rng, stage) {
    if (stage !== 'initial' || ctx.kind !== 'territory' || ctx.defender === null) return;
    const s = ctx.state;
    if (vocationOf(s, ctx.defender) !== 'defense' || s.defenseUsed.includes(ctx.defender)) return;
    s.defenseUsed.push(ctx.defender);
    ctx.notes.push('knightUsed');
    const pairs = Math.min(ctx.attackRolls.length, ctx.defenseRolls.length);
    if (pairs === 0) return;
    const i = pairs - 1;
    const wouldLose = ctx.defenderWinsAll
      ? false
      : ctx.attackRolls[i] > ctx.defenseRolls[i] ||
        (ctx.attackRolls[i] === ctx.defenseRolls[i] && ctx.tieGoesTo === 'attacker');
    if (wouldLose) {
      ctx.defenseRolls[i] = rng.die();
      ctx.notes.push('knight');
    }
  },
};

/** Bênção: o defensor vence todas as comparações no próximo combate contra o território. */
export const blessing: CombatModifier = {
  id: 'blessing',
  rules(ctx) {
    if (ctx.kind !== 'territory' || !ctx.to) return;
    const s = ctx.state;
    const idx = s.effects.findIndex((e) => e.kind === 'blessing' && e.territory === ctx.to);
    if (idx < 0) return;
    s.effects.splice(idx, 1);
    ctx.defenderWinsAll = true;
    ctx.notes.push('blessing');
  },
};

/** Stone Skin: o território perde no máximo 1 tropa por rolagem, atacando ou defendendo. */
export const ward: CombatModifier = {
  id: 'ward',
  outcome(ctx, out) {
    const s = ctx.state;
    if (out.attackerLoss > 1 && hasEffect(s, ctx.from, 'ward')) {
      out.attackerLoss = 1;
      if (!ctx.notes.includes('ward')) ctx.notes.push('ward');
    }
    if (ctx.to && out.defenderLoss > 1 && hasEffect(s, ctx.to, 'ward')) {
      out.defenderLoss = 1;
      if (!ctx.notes.includes('ward')) ctx.notes.push('ward');
    }
  },
};

/** A ordem importa no estágio de rolagem: a runa soma antes de a defesa checar se perderia. */
export const DEFAULT_MODIFIERS: CombatModifier[] = [seaLimit, blessing, precision, strengthRune, coordination, flow, defenseVocation, ward];
